'use client';

import './selection-card.scss';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Prominence, Sizes, Text, Variants } from '@sola/ark-core/atoms/text/text.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { Image } from '../../atoms/image/image.tsx';
import placeholder from './placeholder.png';
type ImageProps = {
  url?: string;
  alt?: string;
};
export type SelectionCardDetails = {
  tinyTopText?: string;
  primaryText?: string;
  primaryBody?: string;
  image?: ImageProps;
  label?: string;
  secondaryTitle?: string;
  secondaryText?: string;
  value: string;
};
type ComputedDisplayContent = {
  hasCheckedContent: boolean;
  tinyTopText?: string;
  contentLabel?: string;
  primaryBody?: string;
  secondaryTitle?: string;
  effectiveSecondaryText?: string;
  showMedia: boolean;
  useCheckedView: boolean;
};
const computeDisplayContent = (details: SelectionCardDetails, isChecked: boolean, hideMediaWhenChecked: boolean): ComputedDisplayContent => {
  const {
    label,
    secondaryText,
    tinyTopText,
    primaryText,
    primaryBody,
    secondaryTitle
  } = details;
  const hasCheckedContent = Boolean(label || secondaryText);

  // When checked and we have "checked" content, show that;
  // otherwise fall back to the unchecked view's primaryText.
  const useCheckedView = isChecked && hasCheckedContent;
  const contentLabel = useCheckedView ? label : undefined;
  const effectiveSecondaryText = useCheckedView ? secondaryText : primaryText;
  const showMedia = !hideMediaWhenChecked && !isChecked || !hasCheckedContent;
  return {
    hasCheckedContent,
    tinyTopText,
    contentLabel,
    primaryBody,
    secondaryTitle,
    effectiveSecondaryText,
    showMedia,
    useCheckedView
  };
};
export type SelectionCardProps = {
  ariaLabel?: string;
  details: SelectionCardDetails;
  /** When true, media is hidden while the card is checked */
  hideMediaWhenChecked?: boolean;
};
export type SelectionCardVariant = 'stacked' | 'horizontal';
export const SelectionCard: React.FC<SelectionCardProps & {
  isChecked: boolean;
  variant: SelectionCardVariant;
}> = ({
  details,
  hideMediaWhenChecked = false,
  isChecked,
  variant = 'stacked'
}) => {
  const {
    t
  } = useTranslation(['help-me']);
  const rootElRef = useRef<HTMLSpanElement | null>(null);
  const [hasReadMore, setHasReadMore] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    image
  } = details;
  const {
    hasCheckedContent,
    tinyTopText,
    contentLabel,
    primaryBody,
    secondaryTitle,
    effectiveSecondaryText,
    showMedia,
    useCheckedView
  } = computeDisplayContent(details, isChecked, hideMediaWhenChecked);
  useEffect(() => {
    if (!rootElRef.current) {
      return;
    }

    // Use clientHeight or offsetHeight to get the element's actual rendered height
    const {
      clientHeight,
      scrollHeight
    } = rootElRef.current as HTMLElement;
    if (!isExpanded) {
      setHasReadMore(scrollHeight > clientHeight);
    }
  }, [useCheckedView, isExpanded]);
  useEffect(() => {
    if (!isExpanded) {
      return;
    }
    setIsExpanded(false);
  }, [isChecked]);
  const imageUrl = image?.url || placeholder;
  const imageAlt = image?.alt || '';
  const rootClasses = cn('SelectionCard block w-full rounded-md', `SelectionCard--${variant}`, {
    'SelectionCard--checked': isChecked
  });
  const layoutClassesByVariant = {
    stacked: 'flex h-full flex-col',
    horizontal: 'flex flex-row justify-between align-center gap-comfy-md w-full h-full p-snug-xl'
  } as const;
  const layoutRootClasses = layoutClassesByVariant[variant];
  const cardId = `selection-card-${details.value}-${useId()}`;
  return <span id={cardId} ref={rootElRef} className={cn(rootClasses, {
    [`SelectionCard--${variant}--expanded h-full flex flex-col justify-space-between`]: isExpanded && contentLabel
  })}>
      <span className={cn(layoutRootClasses)}>
        <span className={cn('SelectionCard__media block', variant === 'stacked' ? 'SelectionCard__media--top' : 'SelectionCard__media--left flex align-center justify-center', {
        hidden: !showMedia
      })}>
          <Image image={{
          url: imageUrl,
          alt: imageAlt
        }} className="SelectionCard__image overflow-hidden w-full h-full object-cover" />
        </span>

        <span className={cn(layoutRootClasses, 'SelectionCard__content-wrapper flex flex-col justify-start w-full h-full py-comfy-lg px-comfy-sm', {
        'p-0 justify-center': variant === 'horizontal'
      })}>
          <span className={cn('SelectionCard__content block', {
          'SelectionCard__content--secondaryContent pt-0 mt-roomy-xs': useCheckedView
        })}>
            {tinyTopText && !useCheckedView && <Text variant={Variants.Label} size={Sizes.Small} prominence={Prominence.Mild}>
                {tinyTopText}
              </Text>}
            {contentLabel && !useCheckedView && <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent}>
                {contentLabel}
              </Text>}
            {primaryBody && !useCheckedView && <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent}>
                {primaryBody}
              </Text>}
            {secondaryTitle && useCheckedView && <Text variant={Variants.Label} size={Sizes.Large} prominence={Prominence.Potent}>
                {secondaryTitle}
              </Text>}
            {effectiveSecondaryText && <Text variant={Variants.Body} size={Sizes.Small}>
                {effectiveSecondaryText}
              </Text>}
          </span>
        </span>
      </span>

      {/* Read More Button */}
      {/* TODO :: do we ONLY want to show this for checked? or should we have this for checked && unchecked? */}
      {/* {hasReadMore && ( */}
      {variant === 'stacked' && contentLabel && (hasReadMore || isExpanded) && <span className={cn('SelectionCard__read-more block bottom-0 left-0 right-0 w-full flex justify-center items-end', {
      // hidden: isExpanded,
      absolute: !isExpanded
    })} aria-hidden="true">
          <ButtonStyle variant="tertiary" size="md">
            <button className="SelectionCard__read-more-button py-comfy-xs px-comfy-sm " type="button" aria-expanded={isExpanded} aria-controls={cardId} onClick={() => setIsExpanded(!isExpanded)}>
              {isExpanded ? t('help-me:readLessText') : t('help-me:readMoreText')}
            </button>
          </ButtonStyle>
        </span>}
    </span>;
};